import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDiscoveryClient } from '../api/context';
import { foldersKey } from '../hooks/folders';
import { leadsKey } from '../hooks/leads';
import type {
  CreatorExportInput,
  CreatorListFilters,
  FolderExportInput,
  LeadListQuery,
  LeadMarkAction,
  ReassignBody,
} from '../api/types';

/**
 * Data hooks for the folder workspace. The paged leads key sits under the
 * existing `leadsKey(folder)` prefix, so everything that already invalidates a
 * folder's leads (a finished search, an export) refreshes the table too.
 */

export const leadsPageKey = (folderPublicId: string, query: LeadListQuery) =>
  [...leadsKey(folderPublicId), 'page', query] as const;

export function useFolderLeadsPage(folderPublicId: string, query: LeadListQuery, enabled = true) {
  const client = useDiscoveryClient();
  return useQuery({
    queryKey: leadsPageKey(folderPublicId, query),
    queryFn: () => client.workspace.leadsPage(folderPublicId, query),
    placeholderData: keepPreviousData,
    enabled,
  });
}

/** Refresh a folder's leads, its counts in the sidebar, and the creators registry. */
function useWorkspaceInvalidator() {
  const qc = useQueryClient();
  return (folderPublicId?: string) => {
    if (folderPublicId) void qc.invalidateQueries({ queryKey: leadsKey(folderPublicId) });
    void qc.invalidateQueries({ queryKey: foldersKey });
    void qc.invalidateQueries({ queryKey: ['discovery', 'registry'] });
  };
}

export function useMarkLeads(folderPublicId: string) {
  const client = useDiscoveryClient();
  const invalidate = useWorkspaceInvalidator();
  return useMutation({
    mutationFn: ({ usernames, action }: { usernames: string[]; action: LeadMarkAction }) =>
      client.workspace.markLeads(folderPublicId, usernames, action),
    onSuccess: () => invalidate(folderPublicId),
  });
}

export function useRemoveLeads(folderPublicId: string) {
  const client = useDiscoveryClient();
  const invalidate = useWorkspaceInvalidator();
  return useMutation({
    mutationFn: (usernames: string[]) => client.leads.deleteBulk(folderPublicId, usernames),
    onSuccess: () => invalidate(folderPublicId),
  });
}

export function useReassignFolder(folderPublicId: string) {
  const client = useDiscoveryClient();
  const invalidate = useWorkspaceInvalidator();
  return useMutation({
    mutationFn: (body: ReassignBody) => client.workspace.reassignFolder(folderPublicId, body),
    onSuccess: () => invalidate(folderPublicId),
  });
}

export function useReassignCreators() {
  const client = useDiscoveryClient();
  const invalidate = useWorkspaceInvalidator();
  return useMutation({
    mutationFn: (body: ReassignBody) => client.workspace.reassignCreators(body),
    onSuccess: () => invalidate(),
  });
}

export function useExportFolder(folderPublicId: string) {
  const client = useDiscoveryClient();
  const invalidate = useWorkspaceInvalidator();
  return useMutation({
    mutationFn: (input: FolderExportInput) => client.workspace.exportFolder(folderPublicId, input),
    onSuccess: () => invalidate(folderPublicId),
  });
}

export function useExportCreators() {
  const client = useDiscoveryClient();
  const invalidate = useWorkspaceInvalidator();
  return useMutation({
    mutationFn: (input: CreatorExportInput) => client.workspace.exportCreators(input),
    onSuccess: () => invalidate(),
  });
}

export const DEFAULT_ARCHIVE_AFTER_COMPLETE_DAYS = 3;

/** `lifecycle.archiveAfterCompleteDays` from Settings, or 3 until it loads / if it can't. */
export function useArchiveAfterCompleteDays(): number {
  const client = useDiscoveryClient();
  const { data } = useQuery({
    queryKey: ['discovery', 'settings', 'values'],
    queryFn: () => client.workspace.settingsValues(),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const v = data?.values?.['lifecycle.archiveAfterCompleteDays'];
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : DEFAULT_ARCHIVE_AFTER_COMPLETE_DAYS;
}

/**
 * Every row matching a filter, fetched a page at a time. For bulk actions
 * whose endpoint only takes explicit usernames (mark, remove, add to folder)
 * when "select all N matching" spans pages that were never loaded.
 */
export async function collectAll<T>(
  fetchPage: (page: number, limit: number) => Promise<{ rows: T[]; total: number }>,
  { limit = 200, max = 5000 }: { limit?: number; max?: number } = {},
): Promise<T[]> {
  const out: T[] = [];
  for (let page = 1; ; page++) {
    const { rows, total } = await fetchPage(page, limit);
    out.push(...rows);
    if (rows.length === 0 || out.length >= total || out.length >= max) break;
  }
  return out.slice(0, max);
}

export type { CreatorListFilters };
